import React, { useState, useEffect } from 'react';
import {
  Plus,
  Grid,
  Sparkles,
  Zap,
  LayoutDashboard,
  Edit3,
  Trash2,
  FolderPlus,
  RefreshCw,
} from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { useRoomStore } from '../store/roomStore';
import { useDashboardStore } from '../store/dashboardStore';
import { useThemeStore } from '../store/themeStore';
import { useRealtimeRooms } from '../hooks/useRealtimeRooms';
import DashboardWidget from './DashboardWidget';
import UserProfileModal from './UserProfileModal';
import { Room } from '../types';
import { useNavigate } from 'react-router-dom';

const CustomDashboard: React.FC = () => {
  const { user } = useAuthStore();
  const { rooms, loading, updateRoomState } = useRoomStore();
  const { theme } = useThemeStore();
  const {
    getUserDashboards,
    getActiveDashboard,
    setActiveDashboard,
    createDashboard,
    updateDashboard,
    deleteDashboard,
    duplicateDashboard,
    addWidget,
    removeWidget,
    moveWidget,
    resizeWidget,
    changeWidgetMode,
    toggleWidgetStateChanges,
    toggleGlobalStateChanges,
    autoArrangeWidgets,
  } = useDashboardStore();

  const [showAddPanel, setShowAddPanel] = useState(false);
  const [showDashboardPanel, setShowDashboardPanel] = useState(false);
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [newDashboardName, setNewDashboardName] = useState('');
  const [editingDashboardId, setEditingDashboardId] = useState<string | null>(
    null
  );
  const [editingDashboardName, setEditingDashboardName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterMode] = useState<'all' | 'visible' | 'hidden'>(
    'all'
  );
  const navigate = useNavigate();

  useRealtimeRooms(); // Set up real-time subscriptions

  const dashboards = user ? getUserDashboards(user.id) : [];
  const activeDashboard = user ? getActiveDashboard(user.id) : null;

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl/Cmd + N for new dashboard
      if ((e.ctrlKey || e.metaKey) && e.key === 'n') {
        e.preventDefault();
        setShowDashboardPanel(true);
      }
      // Ctrl/Cmd + A for auto arrange
      if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
        e.preventDefault();
        handleAutoArrange();
      }
      // Ctrl/Cmd + R for room view
      if ((e.ctrlKey || e.metaKey) && e.key === 'r') {
        e.preventDefault();
        navigate('/');
      }
      // Escape to close panels
      if (e.key === 'Escape') {
        setShowAddPanel(false);
        setShowDashboardPanel(false);
        setShowUserProfile(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate]);

  // Get rooms that are available to add (not already added or hidden)
  const availableRooms = rooms.filter(
    (room) =>
      !activeDashboard?.widgets.some(
        (widget) => widget.roomId === room.id && widget.visible
      )
  );

  // Filter available rooms based on search
  const filteredAvailableRooms = availableRooms.filter(
    (room) =>
      room.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      room.currentState.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Get visible widgets with their corresponding rooms
  const visibleWidgets = activeDashboard
    ? (activeDashboard.widgets
        .filter(
          (widget) =>
            filterMode === 'all' ||
            (filterMode === 'visible' && widget.visible) ||
            (filterMode === 'hidden' && !widget.visible)
        )
        .map((widget) => ({
          widget,
          room: rooms.find((room) => room.id === widget.roomId),
        }))
        .filter((item) => item.room) as Array<{ widget: any; room: Room }>)
    : [];

  const handleAddRoom = (roomId: string) => {
    if (user) {
      addWidget(user.id, roomId);
      setShowAddPanel(false);
    }
  };

  const handleRemoveWidget = (widgetId: string) => {
    if (user) {
      removeWidget(user.id, widgetId);
    }
  };

  const handleMoveWidget = (
    widgetId: string,
    position: { x: number; y: number }
  ) => {
    if (user) {
      moveWidget(user.id, widgetId, position);
    }
  };

  const handleResizeWidget = (widgetId: string, size: any) => {
    if (user) {
      resizeWidget(user.id, widgetId, size);
    }
  };

  const handleModeChange = (widgetId: string, mode: any) => {
    if (user) {
      changeWidgetMode(user.id, widgetId, mode);
    }
  };

  const handleStateChange = (roomId: string, stateId: string) => {
    if (user) {
      updateRoomState(roomId, stateId, user.id);
    }
  };

  const handleAutoArrange = () => {
    if (user) {
      autoArrangeWidgets(user.id);
    }
  };

  const handleToggleWidgetStateChanges = (widgetId: string) => {
    if (user) {
      toggleWidgetStateChanges(user.id, widgetId);
    }
  };

  const handleToggleGlobalStateChanges = () => {
    if (user) {
      toggleGlobalStateChanges(user.id);
    }
  };

  const handleCreateDashboard = () => {
    if (user && newDashboardName.trim()) {
      createDashboard(user.id, newDashboardName.trim());
      setNewDashboardName('');
    }
  };

  const handleUpdateDashboard = (dashboardId: string) => {
    if (user && editingDashboardName.trim()) {
      updateDashboard(user.id, dashboardId, {
        name: editingDashboardName.trim(),
      });
      setEditingDashboardId(null);
      setEditingDashboardName('');
    }
  };

  const handleDeleteDashboard = (dashboardId: string) => {
    if (user && confirm('Are you sure you want to delete this dashboard?')) {
      deleteDashboard(user.id, dashboardId);
    }
  };

  const handleDuplicateDashboard = (dashboardId: string) => {
    if (user) {
      const originalDashboard = dashboards.find((d) => d.id === dashboardId);
      if (originalDashboard) {
        const newName = `${originalDashboard.name} (Copy)`;
        duplicateDashboard(user.id, dashboardId, newName);
      }
    }
  };

  const getUserRoomRole = (room: Room) => {
    const member = room.users.find((u) => u.id === user?.id);
    return member?.role || 'VIEWER';
  };

  const canChangeState = (room: Room) => {
    const role = getUserRoomRole(room);
    return role === 'ADMIN' || role === 'USER';
  };

  if (loading && rooms.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 px-4">
        <div className="text-center">
          <div className="relative">
            <div className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-4 border-blue-200 dark:border-blue-800 border-t-blue-600 dark:border-t-blue-400 mx-auto mb-4 sm:mb-6"></div>
            <div
              className="absolute inset-0 rounded-full h-12 w-12 sm:h-16 sm:w-16 border-4 border-transparent border-t-purple-400 dark:border-t-purple-600 animate-spin mx-auto"
              style={{
                animationDirection: 'reverse',
                animationDuration: '1.5s',
              }}
            ></div>
          </div>
          <p className="text-slate-600 dark:text-slate-400 text-base sm:text-lg font-medium">
            Loading dashboard...
          </p>
          <p className="text-slate-400 dark:text-slate-500 text-sm mt-2">
            Setting up your workspace
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 transition-all duration-300">
      {/* Enhanced Header with Streamlined Navigation */}
      <div className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-xl shadow-lg border-b border-white/20 dark:border-slate-700/50 sticky top-0 z-40">
        <div className="px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex justify-between items-center">
            {/* Logo and Title */}
            <div className="flex items-center space-x-3 sm:space-x-4">
              <div className="flex items-center space-x-2 sm:space-x-3">
                <div className="relative">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg sm:rounded-xl flex items-center justify-center shadow-lg">
                    <LayoutDashboard className="w-5 h-5" />
                  </div>
                  <div className="absolute -top-1 -right-1 w-3 h-3 sm:w-4 sm:h-4 bg-green-400 rounded-full border-2 border-white dark:border-slate-800 animate-pulse"></div>
                </div>
                <div>
                  <h1 className="text-lg sm:text-2xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 dark:from-slate-200 dark:to-slate-400 bg-clip-text text-transparent">
                    {activeDashboard?.name || 'Custom Dashboard'}
                  </h1>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-3">
              <button
                onClick={handleToggleGlobalStateChanges}
                className={`group flex items-center gap-2 px-4 py-2.5 ${
                  activeDashboard?.globalStateChanges
                    ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white'
                    : 'bg-white/70 dark:bg-slate-700/70 text-slate-700 dark:text-slate-300'
                } rounded-xl hover:from-blue-600 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 hover:scale-105`}
                title="Allow widgets to change room states when clicked"
              >
                <RefreshCw className={`w-4 h-4 group-hover:rotate-180 transition-all duration-300 ${
                  activeDashboard?.globalStateChanges ? 'text-white' : 'text-slate-600 dark:text-slate-400'
                }`} />
                <span className="font-medium">State Changes</span>
              </button>
              <button
                onClick={() => {
                  setShowAddPanel(!showAddPanel);
                  setShowDashboardPanel(false);
                }}
                className="group flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 hover:scale-105"
              >
                <Plus className="w-4 h-4 group-hover:rotate-90 transition-all duration-300" />
                <span className="font-medium">Add Room</span>
              </button>
              <button
                onClick={handleAutoArrange}
                className="group flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl hover:from-green-600 hover:to-emerald-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 hover:scale-105"
                title="Auto Arrange (⌘A)"
              >
                <Zap className="w-4 h-4 group-hover:scale-110 group-hover:rotate-12 transition-all duration-300" />
                <span className="font-medium">Auto Arrange</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Expandable Panels - Replacing Nested Menus */}
      {showDashboardPanel && (
        <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl border-b border-white/20 dark:border-slate-700/50 shadow-lg animate-fade-in-down">
          <div className="px-4 sm:px-6 py-4 sm:py-6">
            <div className="flex items-center gap-3 mb-4 sm:mb-6">
              <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-br from-indigo-100 to-purple-200 dark:from-indigo-900 dark:to-purple-900 rounded-lg flex items-center justify-center">
                <FolderPlus className="w-3 h-3 sm:w-4 sm:h-4 text-indigo-600 dark:text-indigo-400" />
              </div>
              <h3 className="text-base sm:text-lg font-bold text-slate-800 dark:text-slate-200">
                Dashboard Management
              </h3>
            </div>

            {/* Create New Dashboard */}
            <div className="mb-6 p-4 bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-700 dark:to-slate-800 rounded-xl border border-slate-200 dark:border-slate-600">
              <h4 className="font-medium text-slate-800 dark:text-slate-200 mb-3">
                Create New Dashboard
              </h4>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newDashboardName}
                  onChange={(e) => setNewDashboardName(e.target.value)}
                  placeholder="Dashboard name"
                  className="flex-1 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 text-sm transition-all duration-200"
                  onKeyPress={(e) =>
                    e.key === 'Enter' && handleCreateDashboard()
                  }
                />
                <button
                  onClick={handleCreateDashboard}
                  disabled={!newDashboardName.trim()}
                  className="px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all duration-200 font-medium disabled:opacity-50 text-sm"
                >
                  Create
                </button>
              </div>
            </div>

            {/* Dashboard List */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {dashboards.map((dashboard) => (
                <div
                  key={dashboard.id}
                  className={`p-4 rounded-xl border-2 transition-all duration-200 ${
                    activeDashboard?.id === dashboard.id
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 hover:border-slate-300 dark:hover:border-slate-500'
                  }`}
                >
                  {editingDashboardId === dashboard.id ? (
                    <div className="space-y-3">
                      <input
                        type="text"
                        value={editingDashboardName}
                        onChange={(e) =>
                          setEditingDashboardName(e.target.value)
                        }
                        className="w-full px-2 py-1 border border-slate-300 dark:border-slate-600 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 transition-all duration-200"
                        onKeyPress={(e) =>
                          e.key === 'Enter' &&
                          handleUpdateDashboard(dashboard.id)
                        }
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleUpdateDashboard(dashboard.id)}
                          className="flex-1 px-2 py-1 bg-green-500 text-white rounded text-sm hover:bg-green-600 transition-colors"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => {
                            setEditingDashboardId(null);
                            setEditingDashboardName('');
                          }}
                          className="px-2 py-1 bg-slate-300 dark:bg-slate-600 text-slate-700 dark:text-slate-300 rounded text-sm hover:bg-slate-400 dark:hover:bg-slate-500 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-slate-800 dark:text-slate-200 truncate">
                          {dashboard.name}
                        </h4>
                        {activeDashboard?.id === dashboard.id && (
                          <span className="text-xs px-2 py-0.5 bg-blue-500 text-white rounded-full">
                            Active
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
                        {dashboard.widgets.filter((w) => w.visible).length}{' '}
                        widgets • Created{' '}
                        {new Date(dashboard.createdAt).toLocaleDateString()}
                      </p>
                      <div className="flex gap-1">
                        {activeDashboard?.id !== dashboard.id && (
                          <button
                            onClick={() =>
                              user && setActiveDashboard(user.id, dashboard.id)
                            }
                            className="flex-1 px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600 transition-colors"
                          >
                            Switch
                          </button>
                        )}
                        <button
                          onClick={() => handleDuplicateDashboard(dashboard.id)}
                          className="p-1 hover:bg-slate-100 dark:hover:bg-slate-600 rounded transition-colors"
                          title="Duplicate"
                        >
                          <svg className="w-3 h-3 text-slate-500 dark:text-slate-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => {
                            setEditingDashboardId(dashboard.id);
                            setEditingDashboardName(dashboard.name);
                          }}
                          className="p-1 hover:bg-slate-100 dark:hover:bg-slate-600 rounded transition-colors"
                          title="Rename"
                        >
                          <Edit3 className="w-3 h-3 text-slate-500 dark:text-slate-400" />
                        </button>
                        {dashboards.length > 1 && (
                          <button
                            onClick={() => handleDeleteDashboard(dashboard.id)}
                            className="p-1 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-3 h-3 text-red-500" />
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Add Room Panel */}
      {showAddPanel && (
        <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl border-b border-white/20 dark:border-slate-700/50 shadow-lg animate-fade-in-down">
          <div className="px-4 sm:px-6 py-4 sm:py-6">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900 dark:to-blue-800 rounded-lg flex items-center justify-center">
                  <Plus className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-base sm:text-lg font-bold text-slate-800 dark:text-slate-200">
                  Add Rooms to Dashboard
                </h3>
              </div>
            </div>
            {filteredAvailableRooms.length === 0 ? (
              <div className="text-center py-6 sm:py-8">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-slate-100 dark:bg-slate-700 rounded-xl sm:rounded-2xl mx-auto mb-3 flex items-center justify-center">
                  <Sparkles className="w-6 h-6 sm:w-8 sm:h-8 text-slate-400" />
                </div>
                <p className="text-slate-500 dark:text-slate-400 font-medium text-sm sm:text-base">
                  {'All available rooms are already added to your dashboard.'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
                {filteredAvailableRooms.map((room) => (
                  <button
                    key={room.id}
                    onClick={() => handleAddRoom(room.id)}
                    className="group flex items-center gap-3 p-3 sm:p-4 border border-slate-200 dark:border-slate-600 rounded-xl hover:bg-white/80 dark:hover:bg-slate-700/80 hover:shadow-lg transition-all duration-200 text-left transform hover:-translate-y-1"
                  >
                    <div className="flex items-center gap-2">
                      {room.currentState.emoji && (
                        <span className="text-lg">
                          {room.currentState.emoji}
                        </span>
                      )}
                      <div
                        className="w-3 h-3 sm:w-4 sm:h-4 rounded-full flex-shrink-0 shadow-sm group-hover:scale-110 transition-transform duration-200"
                        style={{ backgroundColor: room.currentState.color }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 dark:text-slate-100 truncate text-sm sm:text-base">
                        {room.name}
                      </p>
                      <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 truncate">
                        {room.currentState.name}
                      </p>
                    </div>
                    <Plus className="w-3 h-3 sm:w-4 sm:h-4 text-slate-400 group-hover:text-blue-500 group-hover:rotate-90 transition-all duration-200" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Dashboard Canvas */}
      <div className="relative flex-1 p-4 sm:p-6">
        {visibleWidgets.filter(({ widget }) => widget.visible).length === 0 ? (
          <div className="flex flex-col items-center justify-center h-96 text-center px-4">
            <div className="relative mb-6 sm:mb-8">
              <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-700 dark:to-slate-600 rounded-2xl sm:rounded-3xl mx-auto flex items-center justify-center shadow-lg">
                <Grid className="w-10 h-10 sm:w-12 sm:h-12 text-slate-400" />
              </div>
              <div className="absolute -top-2 -right-2 w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-br from-blue-400 to-purple-400 rounded-full flex items-center justify-center shadow-lg">
                <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
              </div>
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-slate-800 dark:text-slate-200 mb-2 sm:mb-3">
              Your Dashboard is Empty
            </h3>
            <p className="text-slate-600 dark:text-slate-400 mb-6 sm:mb-8 max-w-md mx-auto text-sm sm:text-base">
              Add some rooms to create your custom dashboard and start managing
              states with style
            </p>
            <button
              onClick={() => setShowAddPanel(true)}
              className="group inline-flex items-center gap-2 sm:gap-3 px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl sm:rounded-2xl hover:from-blue-600 hover:to-purple-700 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1 text-sm sm:text-lg font-semibold"
            >
              <Plus className="w-5 h-5 sm:w-6 sm:h-6 group-hover:rotate-90 transition-transform duration-200" />
              <span>Add Your First Room</span>
            </button>
          </div>
        ) : (
          <div className="relative min-h-screen">
            {visibleWidgets
              .filter(({ widget }) => widget.visible)
              .map(({ widget, room }) => (
                <DashboardWidget
                  key={widget.id}
                  widget={widget}
                  room={room}
                  onMove={(position) => handleMoveWidget(widget.id, position)}
                  onResize={(size) => handleResizeWidget(widget.id, size)}
                  onModeChange={(mode) => handleModeChange(widget.id, mode)}
                  onRemove={() => handleRemoveWidget(widget.id)}
                  onStateChange={(stateId) =>
                    handleStateChange(room.id, stateId)
                  }
                  onToggleStateChanges={() =>
                    handleToggleWidgetStateChanges(widget.id)
                  }
                  canChangeState={canChangeState(room)}
                  globalStateChanges={
                    activeDashboard?.globalStateChanges || false
                  }
                />
              ))}
          </div>
        )}
      </div>

      <UserProfileModal
        isOpen={showUserProfile}
        onClose={() => setShowUserProfile(false)}
      />
    </div>
  );
};

export default CustomDashboard;